import { useLocation } from 'react-router-dom';
import { AuthApi, type User } from '@up2stay/up2promo-api-client';
import { useEffect } from 'react';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import { DangerZone } from '@/core/components/settings/danger-zone';
import { SettingsPageShell } from '@/core/components/settings/settings-page-shell';
import { AccountSecuritySection } from '@/core/components/user/settings/account-security-section';
import { ContactSection } from '@/core/components/user/settings/contact-section';
import { PhoneSection } from '@/core/components/user/settings/phone-section';
import { ProfileBasicsSection } from '@/core/components/user/settings/profile-basics-section';
import { useErrorMessage } from '@/core/hooks/use-error-message';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useAuthStore } from '@/core/store/auth.store';
import { buildRedirectUrl } from '@/core/utils/redirect-url.utils';
import { AppIonPage } from '@/core/components/app-ion-page';
import { isAxiosError } from 'axios';

/** Owner of a workspace that still has other members (ADR-0017 §3). */
function isOwnershipTransferRequired(error: unknown): boolean {
  if (!isAxiosError<{ errors?: Record<string, string> }>(error)) return false;
  return error.response?.data?.errors?.account === 'ownershipTransferRequired';
}

const SECTION_IDS = {
  profile: 'user-settings-profile',
  contact: 'user-settings-contact',
  phone: 'user-settings-phone',
  security: 'user-settings-security',
  danger: 'user-settings-danger',
} as const;

function UserSettingsBreadcrumb({
  domainLabel,
  settingsLabel,
}: {
  domainLabel: string;
  settingsLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {settingsLabel}
    </>
  );
}

export function UserSettingsPage() {
  const dict = useDictionary();
  const settingsDict = dict.settings;
  const authDict = dict.auth.common;
  const getErrorMessage = useErrorMessage();
  const location = useLocation();

  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);

  const breadcrumb = (
    <UserSettingsBreadcrumb
      domainLabel={dict.nav.account}
      settingsLabel={dict.nav.settings}
    />
  );

  useEffect(() => {
    let cancelled = false;
    void AuthApi.me().then((res) => {
      if (!cancelled && res.data) setUser(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, [setUser]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('section') !== 'phone') {
      return;
    }
    const el = document.getElementById(SECTION_IDS.phone);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.search]);

  const handleUserUpdated = (next: User) => {
    setUser(next);
  };

  const buildRedirectTemplate = () => {
    if (typeof window === 'undefined') return undefined;
    const settingsUrl = buildRedirectUrl(shellPaths.moreUserSettings);
    return buildRedirectUrl(
      `${shellPaths.moreConfirmNewEmail}?hash={{hash}}&redirectUrl=${encodeURIComponent(settingsUrl)}`,
    );
  };

  const handleEmailChange = async (newEmail: string) => {
    if (!user?.id) return;

    const redirectTemplate = buildRedirectTemplate();
    await AuthApi.requestEmailChange({
      body: {
        email: newEmail,
        redirectUrl: redirectTemplate,
      },
      headers: {
        origin: typeof window !== 'undefined' ? window.location.origin : '',
        referer: typeof window !== 'undefined' ? window.location.href : '',
      },
    });
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) return;
    await AuthApi.delete({});
  };

  if (!user) {
    return null;
  }

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader breadcrumb={breadcrumb} title={settingsDict.title} />
          <SettingsPageShell>
            <ProfileBasicsSection
              sectionId={SECTION_IDS.profile}
              user={user}
              onUserUpdated={handleUserUpdated}
              authDict={authDict}
              profileDict={settingsDict.profileBasics}
              avatarDict={settingsDict.avatarUpload}
              saveLabel={settingsDict.save}
              savingLabel={settingsDict.saving}
              usernameTakenLabel={settingsDict.errors.usernameTaken}
            />

            <ContactSection
              sectionId={SECTION_IDS.contact}
              email={user.email || ''}
              onEmailChange={handleEmailChange}
              authDict={authDict}
              settingsDict={settingsDict}
              getErrorMessage={getErrorMessage}
            />

            <PhoneSection
              sectionId={SECTION_IDS.phone}
              phone={user.phone}
              phoneVerified={user.phoneVerified}
              onPhoneChanged={async () => {
                const res = await AuthApi.me();
                if (res.data) setUser(res.data);
              }}
              dict={settingsDict.phone}
              settingsDict={settingsDict}
              smsVerificationAvailable={user.smsVerificationAvailable !== false}
            />

            <AccountSecuritySection
              sectionId={SECTION_IDS.security}
              dict={settingsDict.accountSecurity}
            />

            <DangerZone
              sectionId={SECTION_IDS.danger}
              title={settingsDict.dangerZone.title}
              description={settingsDict.dangerZone.description}
              deleteTitle={settingsDict.accountSecurity.deleteAccountTitle}
              deleteDescription={
                settingsDict.accountSecurity.deleteAccountDescription
              }
              deleteButtonLabel={settingsDict.accountSecurity.deleteAccountLink}
              expandLabel={
                settingsDict.dangerZone.expand ?? 'Show destructive actions'
              }
              collapseLabel={
                settingsDict.dangerZone.collapse ?? 'Hide destructive actions'
              }
              onDelete={handleDeleteAccount}
              resolveErrorMessage={(error) =>
                isOwnershipTransferRequired(error)
                  ? (settingsDict.accountSecurity
                      .deleteAccountOwnershipTransferRequired ?? null)
                  : null
              }
              confirmName={user.email || ''}
              dict={{
                dialogTitle:
                  settingsDict.accountSecurity.deleteAccountDialogTitle,
                dialogDescription:
                  settingsDict.accountSecurity.deleteAccountDialogDescription,
                confirmLabel:
                  settingsDict.accountSecurity.deleteAccountDialogConfirmLabel,
                confirmPlaceholder:
                  settingsDict.accountSecurity
                    .deleteAccountDialogConfirmPlaceholder,
                cancel: settingsDict.accountSecurity.deleteAccountDialogCancel,
                confirm:
                  settingsDict.accountSecurity.deleteAccountDialogConfirm,
                nameMismatch:
                  settingsDict.accountSecurity.deleteAccountDialogEmailMismatch,
                success: settingsDict.accountSecurity.deleteAccountEmailSent,
                error: settingsDict.accountSecurity.deleteAccountError,
              }}
            />
          </SettingsPageShell>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
